import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { chatService, StarterPrompt } from '../services/chatService';
import { Message } from '../types/chat';

// Query keys
export const queryKeys = {
  chats: (userId: number | undefined) => ['chats', userId] as const,
  chat: (id: string) => ['chat', id] as const,
  starterPrompts: ['starterPrompts'] as const,
};


// Types
export interface ChatSummary {
  id: string;
  title: string;
  timestamp: string; // Store as ISO string for proper serialization
  preview: string;
}

interface ChatHistoryResponse {
  history: Message[];
  status: string;
  last_history_id: number;
  agent?: string | null;
}

// Hook for fetching all chats (Home page)
export const useChats = (userId: number | undefined) => {
  return useQuery({
    queryKey: queryKeys.chats(userId),
    queryFn: async (): Promise<ChatSummary[]> => {
      const apiChats = await chatService.fetchChats(20);
      return apiChats.map((chat) => ({
        id: chat.id.toString(),
        title: chat.title || `گفت‌وگوی ${chat.id}`,
        timestamp: chat.created_at, // Keep as ISO string
        preview: chat.status === 'processing' ? 'در حال پردازش...' : 'برای دیدن گفت‌وگو بزن اینجا'
      }));
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnMount: true,
    refetchOnReconnect: true,
    enabled: userId !== undefined,
  });
};

export const useDeleteChat = (userId: number | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chatId: number) => chatService.deleteChat(chatId),
    onSuccess: (_data, chatId) => {
      queryClient.setQueryData<ChatSummary[]>(queryKeys.chats(userId), (current = []) =>
        current.filter((chat) => Number(chat.id) !== chatId),
      );
      queryClient.removeQueries({ queryKey: queryKeys.chat(chatId.toString()) });
      queryClient.invalidateQueries({ queryKey: queryKeys.chats(userId) });
    },
  });
};

// Hook for fetching starter prompts
export const useStarterPrompts = () => {
  return useQuery({
    queryKey: queryKeys.starterPrompts,
    queryFn: async (): Promise<StarterPrompt[]> => {
      return chatService.fetchStarterPrompts();
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - prompts don't change often
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

// Hook for fetching chat history
export const useChatHistory = (chatId: string | undefined, lastHistoryId?: number) => {
  return useQuery({
    queryKey: queryKeys.chat(chatId || ''),
    queryFn: async (): Promise<ChatHistoryResponse> => {
      if (!chatId) throw new Error('Chat ID is required');
      return chatService.fetchChatHistory(parseInt(chatId), lastHistoryId);
    },
    enabled: !!chatId,
    staleTime: 30 * 1000, // 30 seconds - show cached data, refetch in background
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    refetchOnMount: false, // Don't show loading, use cached data
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

// Hook for creating a new chat
export const useCreateChat = (userId: number | undefined) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ message, metadata, sessionId, agent }: { 
      message: string; 
      metadata: Record<string, string>; 
      sessionId: string | null;
      agent?: string | null;
    }) => {
      return chatService.createChat(message, metadata, sessionId || '', agent);
    },
    onSuccess: () => {
      // Invalidate chats list to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.chats(userId) });
    },
  });
};

// Hook for sending a message
export const useSendMessage = () => {
  const queryClient = useQueryClient();
  
  return useMutation<any, unknown, { chatId: number; message: string }>({
    mutationFn: async ({ chatId, message }: { chatId: number; message: string }) => {
      return chatService.sendMessage(chatId, message);
    },
    onSuccess: (_: any, variables: { chatId: number }) => {
      // Invalidate the specific chat to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.chat(variables.chatId.toString()) });
    },
  });
};

// Utility hook to update chat cache manually (for polling updates)
export const useUpdateChatCache = () => {
  const queryClient = useQueryClient();
  
  return {
    updateMessages: (chatId: string, data: ChatHistoryResponse) => {
      queryClient.setQueryData(queryKeys.chat(chatId), data);
    },
    appendMessages: (chatId: string, newMessages: Message[], newStatus: string, lastHistoryId: number) => {
      queryClient.setQueryData(queryKeys.chat(chatId), (old: ChatHistoryResponse | undefined) => {
        if (!old) return { history: newMessages, status: newStatus, last_history_id: lastHistoryId };
        return {
          ...old,
          history: [...old.history, ...newMessages],
          status: newStatus,
          last_history_id: lastHistoryId,
        };
      });
    },
  };
};
